import "mapbox-gl/dist/mapbox-gl.css";

import {withRouter} from "next/router";
import {NextSeo} from "next-seo";
import React from "react";
import ReactMapboxGl, {Marker, ZoomControl} from "react-mapbox-gl";
import styled from "styled-components";

import {send_message} from "../../api/contact";
import InstagramIcon from "../../assets/img/instagram.svg";
import LinkedInIcon from "../../assets/img/linkedin.svg";
import WhatsappIcon from "../../assets/img/whatsapp.svg";
import YoutubeIcon from "../../assets/img/youtube.svg";
import Page from "../../components/Page";
import {device} from "../../constants/media";
import t from "../../utils/locale";

const Container = styled.div`
  display: block;
  overflow: hidden;
  width: 100%;
  min-height: calc(100vh - 100px);
  padding-bottom: 2rem;
`;

const Content = styled.div`
  width: 90%;
  max-width: 910px;
  margin: auto;
  margin-top: 20px;
  border-top: solid 3px #000;
  border-bottom: solid 3px #000;
`;

const Details = styled.div`
  @media ${device.below.tablet} {
    flex-direction: column;
  }

  padding: 1rem;
  display: flex;
`;

const ContactInfo = styled.div`
  & h3,
  h4 {
    margin: 0;
  }

  & p {
    margin-top: 0.5rem;
  }
`;

const ContactForm = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: 1rem;
`;

const Input = styled.input`
  width: 100%;
  height: 100%;
  background: transparent;
  border: none;
  font-size: 12px;
  text-transform: uppercase;
  padding: 1rem;

  &::placeholder {
    color: #999;
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  background: transparent;
  border: none;
  font-size: 12px;
  font-family: Arial, Helvetica, sans-serif;
  min-height: 200px;
  padding: 0.5rem;

  &::placeholder {
    color: #999;
  }
`;

const Button = styled.button`
  width: 100%;
  height: 40px;
  margin: auto;
  margin-top: 1rem;
  background-color: #f4f4f4;
  border: none;
  font-size: 12px;
  text-transform: uppercase;
  color: #666;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    color: #f4f4f4;
    background-color: #666;
  }

  &:focus {
    color: #eee;
    background-color: #666;
  }
`;

const FormInput = styled.div`
  width: 100%;
  min-height: 40px;
  height: auto;
  background-color: #f4f4f4;
  margin-top: 5px;
  border: solid 1px #e4e4e4;
  text-align: center;

  &.textarea {
    min-height: 200px;
  }
`;

const SocialWrapper = styled.div`
  display: flex;
  margin-top: 0.5rem;
`;

const SuccessAlert = styled.div`
  width: 100%;
  text-align: center;
  padding: 1rem;
  color: #259844;
  user-select: none;
  height: 60px;
`;

const ErrorAlert = styled.div`
  width: 100%;
  text-align: center;
  padding: 1rem;
  color: #d03636;
  user-select: none;
  height: 60px;
`;

const PlaceholderAlert = styled.div`
  width: 100%;
  text-align: center;
  padding: 1rem;
  color: #d03636;
  user-select: none;
  height: 60px;
`;

const SocialLink = styled.a`
  margin: 0 0.5rem;
  text-decoration: inherit;
  border: none;
  color: #888;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const SocialIcon = styled.img`
  height: 1.2rem;
`;

const CopyWrite = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Map = ReactMapboxGl({
  accessToken:
    "pk.eyJ1IjoibWFsaXZpeCIsImEiOiJja2hrZTV1aWcwaTc5MnJvaGs2aW5rOWF2In0.Y6jwlLeA1RxiLi1WksnMkw",
});

const Ltr = styled.p`
  direction: ltr !important;
`;

class Contact extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      name: "",
      message: "",
      email: "",
      success: false,
      error: false,
      loading: false,
    };

    this.handleChange = this.handleChange.bind(this);
    this.submit = this.submit.bind(this);
  }

  handleChange(e) {
    const name = e.target.name;
    this.setState({[name]: e.target.value});
  }

  async submit() {
    if (this.state.loading) return;

    if (
      this.state.name.length === 0 ||
      this.state.message.length === 0 ||
      this.state.email.length === 0
    )
      return;

    this.setState({loading: true});
    const result = await send_message({
      name: this.state.name,
      message: this.state.message,
      email: this.state.email,
    });

    if (result.status === 201) {
      await this.setState({
        name: "",
        message: "",
        email: "",
        success: true,
        error: false,
      });
    } else {
      await this.setState({
        success: false,
        error: true,
      });
    }

    this.setState({
      loading: false,
    });
  }

  render() {
    const current_language = this.props.router.locale;

    return (
      <Page>
        <NextSeo title="Contact" description="Contact us." />

        <Container>
          <Map
            style="mapbox://styles/mapbox/streets-v9"
            containerStyle={{
              height: "300px",
              width: "100%",
            }}
            zoom={[15]}
            center={[51.424, 35.7053]}
          >
            <ZoomControl />
            <Marker coordinates={[51.424, 35.7053]} anchor="bottom">
              <b>⊙{t(current_language, "ZAV Architects")}</b>
            </Marker>
          </Map>
          <Content>
            <h3>{t(current_language, "Contact")}</h3>
            <Details>
              <ContactInfo>
                <h4>{t(current_language, "Address")}</h4>
                <p>
                  {t(current_language, "_Address")}
                  <br />
                  {t(current_language, "PostalCode")}:{" "}
                  {t(current_language, "_PostalCode")}
                </p>
                <h4>{t(current_language, "Telephone")}</h4>
                <Ltr>
                  +98 21 8834 6886 <br />
                  +98 21 8834 6887 <br />
                  +98 21 8830 5811 <br />
                  +98 902 134 6886
                </Ltr>
                <h4>{t(current_language, "Email")}</h4>
                <Ltr>
                  info@zavarchitect.com <br />
                  zavarchitects@gmail.com
                </Ltr>
                <h4>{t(current_language, "Social Networks")}</h4>
                <SocialWrapper>
                  <SocialLink
                    target="_blank"
                    href="https://www.instagram.com/zavarchitects/"
                  >
                    <SocialIcon href="/" src={InstagramIcon} />
                  </SocialLink>
                  <SocialLink
                    target="_blank"
                    href="https://www.youtube.com/channel/UCcFHzgeojVdODGQO_X_XGsg"
                  >
                    <SocialIcon href="/" src={YoutubeIcon} />
                  </SocialLink>
                  <SocialLink
                    target="_blank"
                    href="https://www.linkedin.com/company/zav-architects/"
                  >
                    <SocialIcon href="/" src={LinkedInIcon} />
                  </SocialLink>
                  <SocialLink target="_blank" href="https://wa.me/989021346886/">
                    <SocialIcon href="/" src={WhatsappIcon} />
                  </SocialLink>
                </SocialWrapper>
              </ContactInfo>

              <ContactForm>
                <FormInput>
                  <Input
                    type="text"
                    name="name"
                    id="name"
                    value={this.state.name}
                    placeholder={t(current_language, "Name")}
                    onChange={this.handleChange}
                  />
                </FormInput>
                <FormInput>
                  <Input
                    type="email"
                    name="email"
                    id="email"
                    value={this.state.email}
                    placeholder={t(current_language, "Email")}
                    onChange={this.handleChange}
                  />
                </FormInput>
                <FormInput className="textarea">
                  <Textarea
                    name="message"
                    id="message"
                    value={this.state.message}
                    placeholder={t(current_language, "Message")}
                    onChange={this.handleChange}
                  />
                </FormInput>
                <Button onClick={this.submit}>{t(current_language, "Send")}</Button>

                {!this.state.error && !this.state.success && <PlaceholderAlert />}

                {this.state.success && (
                  <SuccessAlert>
                    {t(current_language, "Your message has been sent")}
                  </SuccessAlert>
                )}

                {this.state.error && (
                  <ErrorAlert>{t(current_language, "Failed to send message")}</ErrorAlert>
                )}
              </ContactForm>
            </Details>
          </Content>
          <CopyWrite>
            {t(current_language, "COPYRIGHT_MESSAGE")}
            <a rel="license" href="http://creativecommons.org/licenses/by-nc-nd/4.0/">
              <img
                alt="Creative Commons License"
                style={{borderWidth: 0}}
                src="https://licensebuttons.net/l/by-nc-nd/4.0/80x15.png"
              />
            </a>
          </CopyWrite>
        </Container>
      </Page>
    );
  }
}

export default withRouter(Contact);
